import {
    AsyncOperationId,
    FileEntityId,
    formatFileSize,
    MAX_PHOTO_UPLOAD_FILE_BYTES,
    MAX_PHOTO_UPLOAD_FILE_COUNT,
} from "@sp-crm/core";
import { z } from "zod";

const genericFailureMessage = "Photo upload failed. Please try again.";
const connectionFailureMessage =
    "Photo upload failed. Please check your connection and try again.";

export const photoUploadFailureSchema = z.object({
    originalFilename: z.string(),
    reason: z.string(),
});

export const photoUploadOperationResultSchema = z.object({
    photosAdded: z.number(),
    failures: z.array(photoUploadFailureSchema),
});

export type PhotoUploadOperationResult = z.infer<typeof photoUploadOperationResultSchema>;

const startPhotoUploadResponseSchema = z.object({
    operationId: z.string().min(1),
});

export interface SelectedPhotoFile {
    name: string;
    size: number;
}

export type PhotoSelectionValidation =
    | { kind: "ok" }
    | { kind: "invalid"; message: string };

export function validatePhotoSelection(
    files: SelectedPhotoFile[],
): PhotoSelectionValidation {
    if (files.length === 0) {
        return { kind: "invalid", message: "No photos were selected." };
    }
    if (files.length > MAX_PHOTO_UPLOAD_FILE_COUNT) {
        return {
            kind: "invalid",
            message: `You can add up to ${MAX_PHOTO_UPLOAD_FILE_COUNT} photos at a time. You selected ${files.length}. Please select fewer photos and try again.`,
        };
    }
    const oversizeFiles = files.filter(file => file.size > MAX_PHOTO_UPLOAD_FILE_BYTES);
    if (oversizeFiles.length > 0) {
        const described = oversizeFiles
            .map(file => `${file.name} (${formatFileSize(file.size)})`)
            .join(", ");
        return {
            kind: "invalid",
            message: `Each photo must be under ${formatFileSize(MAX_PHOTO_UPLOAD_FILE_BYTES)}. Too large: ${described}. Please resize these photos and try again.`,
        };
    }
    return { kind: "ok" };
}

export interface PhotoUploadOutcome {
    tone: "success" | "warning" | "error";
    message: string;
}

function photosAddedMessage(photosAdded: number): string {
    return photosAdded === 1 ? "1 photo added." : `${photosAdded} photos added.`;
}

export function photoUploadOutcome(result: unknown): PhotoUploadOutcome {
    const parsed = photoUploadOperationResultSchema.safeParse(result);
    if (!parsed.success) {
        return { tone: "success", message: "Photos added." };
    }
    const { photosAdded, failures } = parsed.data;
    if (failures.length === 0) {
        return { tone: "success", message: photosAddedMessage(photosAdded) };
    }
    const failureList = failures
        .map(failure => `"${failure.originalFilename}": ${failure.reason}`)
        .join("; ");
    if (photosAdded === 0) {
        return { tone: "error", message: `No photos were added. ${failureList}` };
    }
    return {
        tone: "warning",
        message: `${photosAddedMessage(photosAdded)} Couldn't add ${failureList}`,
    };
}

export function photoUploadLimitsDescription(): string {
    return `Up to ${MAX_PHOTO_UPLOAD_FILE_COUNT} photos at a time, ${formatFileSize(MAX_PHOTO_UPLOAD_FILE_BYTES)} each.`;
}

export type PhotoUploadFetch = (input: string, init: RequestInit) => Promise<Response>;

export async function requestPhotoUpload(
    fetchFn: PhotoUploadFetch,
    entityId: FileEntityId,
    photos: File[],
): Promise<AsyncOperationId> {
    const formData = new FormData();
    photos.forEach(photo => formData.append("photos", photo));

    let response: Response;
    try {
        response = await fetchFn(`/api/entities/${entityId}/photo-uploads`, {
            method: "POST",
            body: formData,
            credentials: "include",
        });
    } catch {
        throw new Error(connectionFailureMessage);
    }

    if (!response.ok) {
        throw new Error(await errorResponseMessage(response));
    }

    const parsed = startPhotoUploadResponseSchema.safeParse(await readJsonBody(response));
    if (!parsed.success) {
        throw new Error(genericFailureMessage);
    }
    return parsed.data.operationId as AsyncOperationId;
}

async function readJsonBody(response: Response): Promise<unknown> {
    return await response.json().catch((): null => null);
}

const errorResponseBodySchema = z.object({
    msg: z.string().optional(),
    customError: z.string().optional(),
    error: z.string().optional(),
});

async function errorResponseMessage(response: Response): Promise<string> {
    const parsed = errorResponseBodySchema.safeParse(await readJsonBody(response));
    if (!parsed.success) {
        return genericFailureMessage;
    }
    return (
        parsed.data.msg ||
        parsed.data.customError ||
        parsed.data.error ||
        genericFailureMessage
    );
}

export function startPhotoUpload(
    entityId: FileEntityId,
    photos: File[],
): Promise<AsyncOperationId> {
    return requestPhotoUpload(
        (input, init) => window.fetch(input, init),
        entityId,
        photos,
    );
}
